import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ApiError,
  type CurrentUser,
  type NotificationSettings,
  notificationSettings,
  saveEscalationPhone,
  testNotification,
} from '../api.ts';
import { Announcer } from '../components/Announcer.tsx';
import { InfoTip } from '../components/InfoTip.tsx';
import { apiMessage } from '../messages.ts';
import { testMessage } from '../notify-text.ts';
import { preferenceEnabled, setPreference } from '../preferences.ts';
import {
  disablePush,
  enablePush,
  isStandalone,
  needsHomeScreen,
  type PushState,
  pushStateFrom,
} from '../push.ts';
import {
  playRing,
  setSoundEnabled,
  soundEnabled,
  soundSupported,
  soundUnlocked,
  unlockSound,
} from '../sound.ts';
import { wakeLockHeld, wakeLockSupported } from '../wake-lock.ts';

/**
 * "Notifiche".
 *
 * "Il test di notifica va fatto al primo turno: è lì che si scopre se qualcosa
 * non funziona, non con una persona in linea" (mock 2). La schermata è
 * costruita attorno a quella frase: prima **come sei avvisato adesso**, riga
 * per riga e sempre a parole — mai un pallino verde e basta — poi il pulsante
 * di prova, e sotto ogni esito il rimedio corrispondente.
 *
 * Quello che non c'è, di proposito: nessun modo di notificare l'utente finale.
 * Le push riguardano solo lo staff (vincolo 9, sezione 7ter).
 */

type Props = {
  user: CurrentUser;
  headingRef?: React.RefObject<HTMLHeadingElement | null>;
};

type Row = {
  id: string;
  label: string;
  state: string;
  action?: { label: string; onClick: () => void };
};

/**
 * Le righe che dipendono dal server, finche' il server non ha risposto.
 *
 * `smsStateLabel` diceva "Nessun numero" e la riga delle notifiche diceva "Non
 * attive" **mentre la lettura era ancora in volo**: due affermazioni false
 * nella mezza schermata che si legge per prima, e proprio quelle due che una
 * persona al primo turno apre la schermata per verificare. Adesso lo stato di
 * quelle due righe e' "lo stiamo leggendo", e il pulsante che le cambia
 * compare quando c'e' qualcosa da cambiare.
 */

export function NotificationsPage({ user, headingRef }: Props) {
  const { t } = useTranslation();

  const [settings, setSettings] = useState<NotificationSettings>();
  const [pushState, setPushState] = useState<PushState>('off');
  const [sound, setSound] = useState(soundEnabled);
  const [shortcuts, setShortcuts] = useState(() => preferenceEnabled('console.shortcuts'));
  /**
   * Ciò che dipende dal dispositivo e non dal server.
   *
   * Letto in uno stato, non calcolato durante il disegno: `soundUnlocked()`,
   * `wakeLockHeld()` e `isStandalone()` vivono fuori da React, e leggerli nel
   * corpo del componente significa mostrare quello che era vero all'ultimo
   * disegno provocato da altro — una riga che dice "non attivo" per sempre.
   */
  const [device, setDevice] = useState(readDevice);
  const refreshDevice = useCallback(() => setDevice(readDevice()), []);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [help, setHelp] = useState<string>();
  const [announcement, setAnnouncement] = useState('');
  const [seq, setSeq] = useState(0);

  const phoneRef = useRef<HTMLInputElement>(null);

  /** Un esito si vede **e** si sente: lo schermo non è l'unico canale. */
  const say = useCallback((message: string, remedy?: string) => {
    setNotice(message);
    setHelp(remedy);
    setAnnouncement(remedy ? `${message} ${remedy}` : message);
    setSeq((value) => value + 1);
  }, []);

  /**
   * L'aggiunta alla schermata Home può avvenire **mentre** questa schermata è
   * aperta: è proprio la cosa che la schermata chiede di fare. Il resto cambia
   * solo per una nostra azione, e lì lo rileggiamo a mano.
   */
  useEffect(() => {
    let media: MediaQueryList | undefined;
    try {
      media = globalThis.matchMedia?.('(display-mode: standalone)');
    } catch {
      media = undefined;
    }
    media?.addEventListener('change', refreshDevice);
    return () => media?.removeEventListener('change', refreshDevice);
  }, [refreshDevice]);

  const load = useCallback(async (options: { keepPushState?: boolean } = {}) => {
    try {
      const loaded = await notificationSettings();
      setSettings(loaded);
      // Uno stato che si scopre solo provando non si lascia sovrascrivere da
      // una rilettura del server, che quel problema non lo può vedere.
      setPushState((previous) =>
        options.keepPushState && previous === 'no_worker' ? previous : pushStateFrom(loaded),
      );
    } catch {
      // Una lettura persa non cambia niente di ciò che è attivo: si tiene
      // quello che si sa già, invece di far sparire dalla schermata il numero
      // e lo stato dell'iscrizione come se non ci fossero mai stati.
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const enable = async (): Promise<void> => {
    if (busy) return;
    const publicKey = settings?.publicKey;
    if (!publicKey) {
      say(t('notifications.push_not_configured'), apiMessage(t, 'errors.push_not_configured'));
      return;
    }
    setBusy(true);
    /*
      L'attesa può durare gli otto secondi del tetto di `readyRegistration`, e
      in quel tempo cambia solo l'etichetta del pulsante: chi non guarda lo
      schermo non avrebbe niente. Va nella regione live, non a schermo — una
      riga in più qui è esattamente ciò che AQ5 ha appena tolto.
    */
    say(t('notifications.enabling'));
    try {
      const state = await enablePush(publicKey);
      setPushState(state);
      if (state === 'on') say(t('notifications.enabled'));
      else if (state === 'denied')
        say(t('notifications.push_denied'), t('notifications.denied_help'));
      else if (state === 'unsupported')
        say(t('notifications.push_unsupported'), t('notifications.ios_hint'));
      // Il service worker non è arrivato in servizio: il rimedio è ricaricare
      // o uscire dalla finestra privata, non "aggiungi alla schermata Home".
      // Un rimedio sbagliato manda una persona a fare la cosa che non serve, e
      // la lascia convinta di essere avvisata (AQ3).
      else if (state === 'no_worker')
        say(t('notifications.push_no_worker'), t('notifications.no_worker_help'));
      /*
        `off` non è per forza un guasto: è anche ciò che torna quando il
        permesso non risulta "granted" senza essere "denied" — il caso più
        comune è che il browser non abbia nemmeno mostrato la richiesta
        perché le notifiche sono disattivate a livello di sistema operativo
        per quel browser, non solo per questo sito. Prima finiva nello stesso
        "non siamo riusciti" generico di un errore vero, senza dire cosa
        controllare (segnalato dal proprietario dopo una prova in
        produzione, `docs/DECISIONI.md`).
      */ else if (state === 'off')
        say(t('notifications.enable_failed'), t('notifications.permission_not_granted_help'));
      else say(t('notifications.enable_failed'));
    } catch (error) {
      // Senza questo, un'eccezione vera (es. il servizio push del browser
      // che rifiuta l'iscrizione) spariva senza lasciare traccia: la
      // schermata mostra solo il messaggio neutro, ma chi guarda gli
      // strumenti di sviluppo del browser deve poter vedere cosa è successo
      // davvero, la prossima volta che càpita.
      console.error('notifiche: attivazione fallita', error);
      say(t('notifications.enable_failed'));
    } finally {
      setBusy(false);
      /*
        `load` rilegge dal server e ricalcola lo stato della riga, ma
        `pushStateFrom` non conosce `no_worker` — è una cosa che si sa solo
        provando — e riporterebbe la riga a "Non attive" mentre il riquadro qui
        sotto dice perché non è riuscita: due risposte diverse alla stessa
        domanda nella stessa schermata.
      */
      void load({ keepPushState: true });
    }
  };

  const disable = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      setPushState(await disablePush());
      say(t('notifications.disabled'));
    } catch {
      say(t('notifications.enable_failed'));
    } finally {
      setBusy(false);
      void load();
    }
  };

  /**
   * La prova, con il rimedio attaccato all'esito.
   *
   * Tre esiti diversi hanno tre cause diverse — nessun dispositivo iscritto, il
   * servizio push che non risponde, le chiavi che mancano sul server — e dire
   * "non ha funzionato" lascerebbe una persona al primo turno a indovinare.
   */
  const test = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      const { outcome } = await testNotification();
      say(...testMessage(outcome, t));
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(t('notifications.test_failed'), apiMessage(t, key));
    } finally {
      setBusy(false);
    }
  };

  const toggleSound = (): void => {
    const next = !sound;
    setSoundEnabled(next);
    setSound(next);
    if (next) {
      // Questo clic **è** il gesto che sblocca l'audio: sprecarlo vorrebbe
      // dire un primo squillo muto e nessuno che capisce perché.
      void unlockSound().then(() => {
        playRing();
        refreshDevice();
      });
    }
    say(next ? t('notifications.sound_on') : t('notifications.sound_off'));
  };

  const toggleShortcuts = (): void => {
    const next = !shortcuts;
    setPreference('console.shortcuts', next);
    setShortcuts(next);
    say(next ? t('notifications.shortcuts_on') : t('notifications.shortcuts_off'));
  };

  const tryTheSound = async (): Promise<void> => {
    // `resume()` non è istantanea su Safari e su iOS: leggere lo stato subito
    // dopo diceva "si sblocca entrando in turno" a chi lo aveva appena
    // sbloccato — una diagnosi falsa con il rimedio giusto attaccato.
    const unlocked = await unlockSound();
    playRing();
    refreshDevice();
    say(unlocked ? t('notifications.sound_on') : t('notifications.sound_locked'));
  };

  const savePhone = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    try {
      const value = phone.trim();
      const result = await saveEscalationPhone(value.length > 0 ? value : null);
      setSettings((previous) =>
        previous ? { ...previous, escalationPhoneHint: result.escalationPhoneHint } : previous,
      );
      setPhone('');
      // Il campo si svuota e il pulsante "salva" sparisce da sotto le dita: il
      // fuoco va sul campo, che è l'unica cosa da cui si può ripartire.
      phoneRef.current?.focus();
      say(value.length > 0 ? t('notifications.phone_saved') : t('notifications.phone_removed'));
    } catch (error) {
      const key = error instanceof ApiError ? error.messageKey : 'errors.unexpected';
      say(t('notifications.phone_failed'), apiMessage(t, key));
      phoneRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const removePhone = async (): Promise<void> => {
    if (busy) return;
    setPhone('');
    setBusy(true);
    try {
      await saveEscalationPhone(null);
      setSettings((previous) => (previous ? { ...previous, escalationPhoneHint: null } : previous));
      say(t('notifications.phone_removed'));
    } catch {
      say(t('notifications.phone_failed'));
    } finally {
      setBusy(false);
      phoneRef.current?.focus();
    }
  };

  /** Il server non ha ancora risposto: le sue due righe non affermano niente. */
  const loading = settings === undefined;

  const rows: Row[] = [
    {
      id: 'push',
      label: t('notifications.push_label'),
      state: loading ? t('notifications.state_loading') : pushStateLabel(pushState, t),
      ...(loading
        ? {}
        : pushState === 'on'
          ? {
              action: {
                label: t('notifications.disable'),
                onClick: () => void disable(),
              },
            }
          : /*
             `no_worker` tiene il pulsante, e non è simmetria per bellezza.
             Il suo rimedio dice "ricarica e **riprova**": un pulsante che
             sparisce mentre ha il fuoco lo scarica sul corpo del documento
             (AP4) e porta via l'unico modo di fare ciò che si è appena letto.
             `denied` e `unsupported` restano senza, e lì è giusto: sono vicoli
             ciechi del browser, non cose da ritentare da qui.
          */
            pushState === 'off' || pushState === 'no_worker'
            ? {
                action: {
                  // Cambia etichetta invece di disabilitarsi: un pulsante che si
                  // disabilita mentre lavora si porta via il fuoco (U2).
                  label: busy ? t('notifications.enabling') : t('notifications.enable'),
                  onClick: () => void enable(),
                },
              }
            : {}),
    },
    {
      id: 'sound',
      label: t('notifications.sound_label'),
      state: soundStateLabel(sound, device, t),
      ...(soundSupported()
        ? {
            action: {
              label: sound ? t('notifications.sound_disable') : t('notifications.sound_enable'),
              onClick: toggleSound,
            },
          }
        : {}),
    },
    {
      /**
       * Le scorciatoie si possono spegnere.
       *
       * Una scorciatoia fatta di lettere — anche con Maiusc — ricade in WCAG
       * 2.1.4, che chiede di poterla disattivare o rimappare. E non è un
       * criterio astratto: Maiusc+C chiude una conversazione in corso.
       */
      id: 'shortcuts',
      label: t('notifications.shortcuts_label'),
      state: shortcuts ? t('notifications.shortcuts_on') : t('notifications.shortcuts_off'),
      action: {
        label: shortcuts
          ? t('notifications.shortcuts_disable')
          : t('notifications.shortcuts_enable'),
        onClick: toggleShortcuts,
      },
    },
    {
      id: 'home',
      label: t('notifications.home_label'),
      state: device.standalone ? t('notifications.home_done') : t('notifications.home_todo'),
    },
    {
      id: 'wake',
      label: t('notifications.wake_label'),
      state: wakeLockSupported()
        ? device.screenAwake
          ? t('notifications.wake_on')
          : t('notifications.wake_off')
        : t('notifications.wake_unsupported'),
    },
    /*
      L'SMS **non** sta in questo riepilogo.

      Ci stava, e la sua riga diceva esattamente le stesse parole del titolo
      della scheda qui sotto — "SMS se la notifica non arriva" — con accanto lo
      stesso stato che la scheda ripete sotto il campo. Tre copie della stessa
      informazione in mezzo schermo, e la riga era anche una delle tre senza
      nulla da premere, cioè sembrava rotta accanto a quelle con il pulsante.

      La scheda sotto è il posto giusto: ha il titolo, lo stato e il comando
      per cambiarlo. Le altre cinque righe restano perché il loro comando — o
      la loro assenza di comando — è tutto quello che c'è da sapere.
    */
  ];

  return (
    <main className="page" id="main" tabIndex={-1}>
      <h2 ref={headingRef} tabIndex={-1}>
        {t('notifications.title')}
      </h2>

      {/* Sempre montata e inizialmente vuota: una regione live che nasce piena
          non viene annunciata. */}
      <Announcer polite={announcement} assertive="" seq={seq} />

      <section className="card" aria-labelledby="notify-status">
        <div className="section-head">
          <div className="section-head__title">
            <h3 id="notify-status">{t('notifications.status_title')}</h3>
            <InfoTip>{t('notifications.description')}</InfoTip>
          </div>
        </div>

        {/*
          Un elenco di righe come tutti gli altri della console.

          Erano `.settings__row`, cioè etichetta, stato e pulsante in
          `space-between`: le tre righe con un pulsante e le tre senza
          allineavano lo stato in due colonne diverse, e quelle senza azione
          sembravano righe rotte. Con `.row` lo stato sta **sotto** la propria
          etichetta — dove sta in ogni altro elenco del prodotto — e l'azione,
          quando c'è, è l'unica cosa a fine riga.
        */}
        <ul className="plain-list">
          {rows.map((row) => (
            <li key={row.id} className="row">
              <div>
                <p className="row__title">{row.label}</p>
                {/* Lo stato è **scritto**: nessuna informazione affidata al solo
                    colore (sezione 7bis). */}
                <p className="row__meta">{row.state}</p>
              </div>
              {row.action ? (
                <div className="row__actions">
                  <button type="button" className="button" onClick={row.action.onClick}>
                    {row.action.label}
                  </button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>

        {/*
          Subito sotto l'elenco delle righe, non dopo il riquadro "prima di
          attivarle" e le due prove: è qui che sta il pulsante che ha appena
          prodotto questo esito, e chi lo preme deve vederlo senza scorrere
          oltre due blocchi di testo (segnalato dal proprietario dopo una
          prova in produzione — il messaggio "non siamo riusciti" compariva
          troppo più in basso del pulsante "Attiva le notifiche" per essere
          visto subito, `docs/DECISIONI.md`). L'annuncio per chi usa uno
          screen reader lo fa comunque la regione persistente in cima alla
          pagina, non questo riquadro: due regioni live con lo stesso testo
          lo farebbero leggere due volte.
        */}
        {notice ? (
          <p className="banner">
            {notice}
            {help ? <span className="settings__help">{help}</span> : null}
          </p>
        ) : null}

        {/*
          Cosa succede se le attivi, prima di attivarle.

          Sotto i pulsanti restavano quattro paragrafi di seguito tutti dello
          stesso peso — quattrocentottanta caratteri fra i controlli e la scheda
          dell'SMS — e prima ancora erano sette (AQ5). Il problema non era solo
          quanti fossero: era che una conseguenza da leggere **prima** di
          premere e una spiegazione da consultare **dopo** avevano la stessa
          forma, e chi legge poco le salta tutte.

          Qui restano due frasi, in un riquadro con il suo titolo, e stanno
          **sopra** il pulsante invece che sotto: cosa viene registrato presso
          il servizio del browser e su quale dispositivo arrivano (le due erano
          separate e dicevano la stessa cosa da due lati), e cosa la notifica
          non dirà mai su una schermata di blocco che altri possono vedere
          (vincolo 8, sezione 7ter).
        */}
        <div className="note">
          <p>
            <strong>{t('notifications.before_enable')}</strong>
          </p>
          <p>{t('notifications.enable_note')}</p>
          <p>{t('notifications.privacy_note')}</p>
        </div>

        {/*
          Le due prove, in fila e non incolonnate a tutta larghezza.

          Due pulsanti larghi quanto lo schermo, uno sotto l'altro, sono due
          uscite che sembrano la stessa: la prova della notifica è **l'azione**
          di questa schermata — l'unica primaria — e la prova del suono le sta
          accanto.

          Cambiano etichetta, non si disabilitano: un pulsante che si disabilita
          mentre lavora perde il fuoco, e chi naviga da tastiera si ritrova a
          inizio pagina proprio mentre aspetta di leggere l'esito (U2). Il
          doppio tocco lo ferma la guardia dentro il gestore.
        */}
        <div className="actions">
          <button
            type="button"
            className="button button--primary button--big"
            onClick={() => void test()}
          >
            {busy ? t('notifications.testing') : t('notifications.test')}
          </button>
          <button
            type="button"
            className="button"
            onClick={() => void tryTheSound()}
            disabled={!soundSupported()}
          >
            {t('notifications.sound_test')}
          </button>
        </div>

        {/*
          L'unica frase sciolta rimasta, e sta qui di proposito: è la promessa
          che regge tutto il resto — senza notifiche non si perde comunque una
          chiamata (O4) — e serve proprio a chi ha appena letto un esito
          negativo. Chiuderla in un riquadro da aprire la toglierebbe di mano a
          chi ne ha bisogno, e allontanarla dall'esito la farebbe leggere a chi
          non ne ha bisogno.
        */}
        <p className="muted">{t('notifications.safety_net')}</p>
        {!device.standalone && needsHomeScreen() ? (
          <p className="banner">{t('notifications.ios_hint')}</p>
        ) : null}

        {/*
          `details` e `summary` veri: si aprono da tastiera, si annunciano come
          apribili, e il testo dentro resta nell'albero del documento. Un
          riquadro fatto a mano sarebbe da rendere accessibile daccapo.
        */}
        <details className="more">
          <summary>{t('notifications.more')}</summary>
          <p className="muted">{t('notifications.sound_hint')}</p>
          <p className="muted">{t('notifications.shortcuts_hint')}</p>
          <p className="muted">{t('notifications.wake_hint')}</p>
        </details>
      </section>

      <section className="card" aria-labelledby="notify-phone">
        <h3 id="notify-phone">{t('notifications.sms_label')}</h3>
        {/*
          Non dietro l'icona: conferma il vincolo 3 (la piattaforma non
          contatta mai per prima l'utente finale) proprio sopra il modulo che
          aggiunge il numero per l'escalation SMS — stesso criterio di
          `moderation.on_volunteers_hint` in BQ2 (`docs/DECISIONI.md`).
        */}
        <p className="card__sub">{t('notifications.sms_hint')}</p>

        <form onSubmit={(event) => void savePhone(event)}>
          <div className="field">
            <label htmlFor="escalation-phone">{t('notifications.phone_label')}</label>
            <input
              id="escalation-phone"
              ref={phoneRef}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              // Il proprio numero non torna indietro per intero: la console si
              // apre anche da un computer di sede, e le ultime cifre bastano a
              // riconoscerlo.
              placeholder={settings?.escalationPhoneHint ?? undefined}
              aria-describedby="escalation-phone-help"
              onChange={(event) => setPhone(event.target.value)}
              disabled={busy || settings?.smsConfigured === false}
            />
            <p className="muted" id="escalation-phone-help">
              {t('notifications.phone_help')}
            </p>
            {/*
              Lo stato su una riga sua.

              Era attaccato in coda al paragrafo sulla riservatezza, senza
              punteggiatura e senza andare a capo: "…serve solo a mandarti
              l'SMS. Attivo sul numero che finisce per 777". Due frasi di
              natura diversa — una regola e uno stato — cucite in un periodo
              solo che non finisce mai.
            */}
            {settings ? <p className="row__meta">{smsStateLabel(settings, t)}</p> : null}
          </div>

          {/*
            Non primaria: la primaria di questa schermata è la prova della
            notifica, e due accenti su due schede diverse sono due schermate che
            si contendono la stessa attenzione. Togliere il numero è ciò che
            toglie, e lo dice.
          */}
          <div className="actions">
            <button type="submit" className="button" disabled={busy || phone.trim().length === 0}>
              {t('notifications.phone_save')}
            </button>
            {settings?.escalationPhoneHint ? (
              <button
                type="button"
                className="button button--danger"
                onClick={() => void removePhone()}
                disabled={busy}
              >
                {t('notifications.phone_remove')}
              </button>
            ) : null}
          </div>
        </form>
      </section>

      {/* Il nome di chi è entrato resta in cima alla console, non qui: questa
          schermata parla di dispositivi, non di persone. */}
      <p className="visually-hidden">{t('home.greeting', { name: user.displayName })}</p>
    </main>
  );
}

function pushStateLabel(state: PushState, t: (key: string) => string): string {
  const labels: Record<PushState, string> = {
    on: 'notifications.push_on',
    off: 'notifications.push_off',
    denied: 'notifications.push_denied',
    no_worker: 'notifications.push_no_worker',
    unsupported: 'notifications.push_unsupported',
    not_configured: 'notifications.push_not_configured',
  };
  return t(labels[state]);
}

/** Ciò che si può sapere solo guardando questo dispositivo, in un colpo solo. */
type DeviceState = { soundReady: boolean; screenAwake: boolean; standalone: boolean };

function readDevice(): DeviceState {
  return { soundReady: soundUnlocked(), screenAwake: wakeLockHeld(), standalone: isStandalone() };
}

function soundStateLabel(
  enabled: boolean,
  device: DeviceState,
  t: (key: string) => string,
): string {
  // Un browser senza audio non è "attivo": diceva "Attivo" con i due pulsanti
  // spenti e nessuna parola sul perché, come già fanno le altre righe.
  if (!soundSupported()) return t('notifications.sound_unsupported');
  if (!enabled) return t('notifications.sound_off');
  return device.soundReady ? t('notifications.sound_on') : t('notifications.sound_locked');
}

/** Chiamata solo a lettura conclusa: prima di allora la riga non afferma niente. */
function smsStateLabel(
  settings: NotificationSettings | undefined,
  t: (key: string, values?: Record<string, string>) => string,
): string {
  if (settings && !settings.smsConfigured) return t('notifications.sms_not_configured');
  if (!settings?.escalationPhoneHint) return t('notifications.sms_off');
  return t('notifications.sms_on', { digits: settings.escalationPhoneHint });
}
